import * as React from "react";
import { AutoCompleteBorderless } from "@/core/components/ui/autocomplete/autocomplete-borderless";
import { US_STATES } from "@/core/components/ui/autocomplete/us-states";
import { InputBorderless } from "@/core/components/ui/input";
import { IAddress } from "@/core/types/address";
import { cn } from "@/core/utils/utils";

interface AddressInputProps {
  value?: Partial<IAddress>;
  onChange?: (value: IAddress) => void;
  required?: boolean;
  error?: string;
  className?: string;
  labels?: IAddress;
}

export const AddressInput = React.forwardRef<HTMLDivElement, AddressInputProps>(
  (
    {
      value = {},
      onChange,
      labels = {
        line_one: "Address",
        line_two: "Company, C/O, Apt, Suite, Unit",
        city: "City",
        state: "State",
        zip_code: "ZIP",
      },
      required = false,
      error,
      className,
    },
    ref,
  ) => {
    const hasContent = React.useMemo(
      () => ({
        line_one: Boolean(value.line_one),
        line_two: Boolean(value.line_two),
        city: Boolean(value.city),
        state: Boolean(value.state),
        zip_code: Boolean(value.zip_code),
      }),
      [value],
    );

    const hasAnyContent = React.useMemo(
      () => Object.values(hasContent).some(Boolean),
      [hasContent],
    );

    const [stateSearchValue, setStateSearchValue] = React.useState("");

    return (
      <div ref={ref} className={cn("relative min-h-[56px] p-px", "w-full")}>
        <div
          className={cn(
            "flex flex-col rounded-lg border border-input bg-card shadow-sm",
            "focus-within:ring-1 focus-within:ring-ring",
            error && "border-destructive",
            className,
          )}
        >
          <InputBorderless
            label={labels.line_one}
            hasContent={hasContent.line_one}
            error={error}
            required={required || hasAnyContent}
            value={value.line_one || ""}
            onChange={(e) => {
              onChange?.({
                ...(value as IAddress),
                line_one: e.target.value,
              });
            }}
            className="rounded-t-lg"
          />

          <div className="mx-4 h-px bg-stroke-tertiary" />

          <InputBorderless
            label={labels.line_two}
            hasContent={hasContent.line_two}
            error={error}
            value={value.line_two || ""}
            onChange={(e) => {
              onChange?.({
                ...(value as IAddress),
                line_two: e.target.value,
              });
            }}
          />

          <div className="mx-4 h-px bg-stroke-tertiary" />

          <InputBorderless
            label={labels.city}
            hasContent={hasContent.city}
            error={error}
            value={value.city || ""}
            required={required || hasAnyContent}
            onChange={(e) => {
              onChange?.({
                ...(value as IAddress),
                city: e.target.value,
              });
            }}
          />

          <div className="mx-4 h-px bg-stroke-tertiary" />

          <div className="flex">
            <div className="flex-1">
              <AutoCompleteBorderless
                id="state"
                label={labels.state}
                hasContent={hasContent.state}
                error={error}
                required={required || hasAnyContent}
                selectedValue={value.state || ""}
                onSelectedValueChange={(newValue) => {
                  onChange?.({
                    ...(value as IAddress),
                    state: newValue,
                  });
                }}
                searchValue={stateSearchValue}
                onSearchValueChange={setStateSearchValue}
                items={US_STATES}
              />
            </div>
            <div className="mt-2 h-10 w-px bg-stroke-tertiary" />
            <div className="flex-1">
              <InputBorderless
                label={labels.zip_code}
                hasContent={hasContent.zip_code}
                error={error}
                maxLength={5}
                value={value.zip_code || ""}
                required={required || hasAnyContent}
                onChange={(e) => {
                  onChange?.({
                    ...(value as IAddress),
                    zip_code: e.target.value.replace(/\D/g, "").slice(0, 5),
                  });
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  },
);

AddressInput.displayName = "AddressInput";
